import React, { useCallback, useEffect, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { neveroverpayApi } from './neveroverpayApi';
import { dollarsFromCents, stateLabel, workflowLabel } from './neveroverpayCopy';
import BrandMark from './BrandMark';
import ConsentGate from './NeverOverpayConsent';
import { flushWelcomeConsent } from './NeverOverpayWelcome';
import { openPlaidLink } from './plaidLink';
import { motion } from 'framer-motion';
import { ArrowRight, Check, CheckCheck } from 'lucide-react';
import './neveroverpay.css';
import './neveroverpay-ui.css';

const STEP_LABELS = ['Goals', 'Gmail', 'Bank', 'Rules', 'Scan', 'Review'];

const STEPS = ['intent', 'connect', 'bank', 'policy', 'scan', 'review'];

const FALLBACK_GOALS = [
  {
    id: 'refunds',
    label: 'Find missing refunds',
    look_first: ['Return confirmations', 'Refund-issued emails', 'Transaction records without matching credits'],
  },
  {
    id: 'renewals',
    label: 'Stop unwanted renewals',
    look_first: ['Upcoming subscription renewals', 'Annual plan charges', 'Trial expiration notices'],
  },
  {
    id: 'bills',
    label: 'Catch bill increases',
    look_first: ['Price-increase emails', 'Plan change notices', 'Utility and service bills'],
  },
  {
    id: 'duplicates',
    label: 'Find duplicate subscriptions',
    look_first: ['Overlapping memberships', 'Two charges from the same merchant'],
  },
  {
    id: 'all',
    label: 'Track all money leaks',
    look_first: ['Missing refunds', 'Upcoming renewals', 'Bill increases'],
  },
];

function dollarsInput(cents) {
  return Math.round((Number(cents) || 0) / 100);
}

export default function NeverOverpayOnboarding() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [paste, setPaste] = useState('');
  const [picked, setPicked] = useState([]);
  const [policy, setPolicy] = useState({
    renewalAlertDays: 30,
    minimumAlertValueCents: 1000,
    maximumAcceptedPriceIncreasePercent: 10,
    alwaysRequireSendApproval: true,
    allowStoreCreditWithoutApproval: false,
    allowHumanOperator: false,
    preferOriginalPaymentRefund: true,
    alertBeforeReturnWindowsExpire: true,
  });

  const refresh = useCallback(async () => {
    const row = await neveroverpayApi.onboarding();
    setData(row);
    if (Array.isArray(row.goals) && row.goals.length) {
      setPicked((prev) => (prev.length ? prev : row.goals));
    }
    if (row.policy) {
      setPolicy((prev) => ({ ...prev, ...row.policy }));
    }
    return row;
  }, []);

  useEffect(() => {
    refresh()
      .then(() => flushWelcomeConsent((kind) => neveroverpayApi.grantConsent(kind)))
      .catch((err) => setError(err.message || 'Could not start onboarding'));
  }, [refresh]);

  useEffect(() => {
    const status = (data?.scan || {}).status;
    if (!['running', 'deep_running'].includes(status)) return undefined;
    const timer = setInterval(() => {
      refresh().catch(() => {});
    }, 1500);
    return () => clearInterval(timer);
  }, [data?.scan?.status, refresh]);

  useEffect(() => {
    if (data?.step !== 'scan') return undefined;
    const status = (data?.scan || {}).status;
    if (status && status !== 'idle' && status !== 'needs_mail') return undefined;
    if (!data?.gmail?.connected && !data?.plaid?.connected) return undefined;
    neveroverpayApi.onboardingScan().then(setData).catch(() => {});
    return undefined;
  }, [data?.step, data?.gmail?.connected, data?.plaid?.connected, data?.scan?.status]);

  async function run(fn) {
    setBusy(true);
    setError('');
    try {
      const row = await fn();
      if (row) setData(row);
      else await refresh();
    } catch (err) {
      setError(err.message || 'Something went wrong');
    } finally {
      setBusy(false);
    }
  }

  if (data?.complete) {
    return <Navigate to="/app" replace />;
  }

  const step = data?.step === 'welcome' ? 'intent' : (data?.step || 'intent');
  const stepIndex = Math.max(0, STEPS.indexOf(step));
  const goals = (data?.goals_catalog || []).length ? data.goals_catalog : FALLBACK_GOALS;
  const selectableGoals = goals.filter((g) => g.id !== 'all' && g.id !== 'paperwork');
  const allPicked = selectableGoals.length > 0 && selectableGoals.every((g) => picked.includes(g.id));
  const lookFirst = data?.look_first || [];

  return (
    <div className="np-root" data-testid="neveroverpay-onboarding">
      <div className="np-wrap" style={{ maxWidth: 720, paddingTop: 36, paddingBottom: 80 }}>
        <BrandMark to="/" kicker="First savings win" />
        <div className="np-stepper" aria-hidden="true">
          {STEPS.map((id, index) => (
            <span key={id} className={index <= stepIndex ? 'on' : ''}>{index + 1}</span>
          ))}
        </div>
        <div className="np-step-labels" aria-hidden="true">
          {STEP_LABELS.map((label, index) => <span key={label} className={index <= stepIndex ? 'on' : ''}>{label}</span>)}
        </div>
        {error ? <div className="np-error" style={{ marginBottom: 16 }}>{error}</div> : null}

        {step === 'intent' ? (
          <section className="np-card">
            <h1>What do you want BilledTwice to help with?</h1>
            <p className="np-muted">Pick one, a few, or everything. We will not ask you to connect Gmail or a bank until after this.</p>
            <div className="np-choice-stack" style={{ marginTop: 16 }}>
              <button
                type="button"
                className={`np-select-all ${allPicked ? 'on' : ''}`}
                disabled={busy}
                onClick={() => setPicked(allPicked ? [] : selectableGoals.map((g) => g.id))}
                data-testid="onboarding-select-all"
              >
                <span className="np-row"><CheckCheck size={18} color={allPicked ? '#8fdc6b' : '#8b9588'} /> <strong>{allPicked ? 'Everything selected' : 'Select all'}</strong></span>
                <span className="np-muted">{allPicked ? 'We watch every kind of money leak.' : `${selectableGoals.length} goals`}</span>
              </button>
              {selectableGoals.map((item) => {
                const on = picked.includes(item.id);
                return (
                  <motion.button
                    key={item.id}
                    type="button"
                    whileTap={{ scale: 0.98 }}
                    className={`np-choice multi ${on ? 'selected' : ''}`}
                    disabled={busy}
                    aria-pressed={on}
                    data-testid={`onboarding-goal-${item.id}`}
                    onClick={() => setPicked((prev) => (prev.includes(item.id) ? prev.filter((g) => g !== item.id) : [...prev, item.id]))}
                  >
                    <span className="np-box" aria-hidden="true">{on ? <Check size={14} strokeWidth={3} /> : null}</span>
                    <span>
                      <strong>{item.label}</strong>
                      {(item.look_first || []).length ? (
                        <span className="np-muted" style={{ display: 'block', marginTop: 6, fontWeight: 400 }}>
                          We’ll look first for {(item.look_first || []).slice(0, 3).join(', ').toLowerCase()}.
                        </span>
                      ) : null}
                    </span>
                  </motion.button>
                );
              })}
            </div>
            <div className="np-actions">
              <button
                type="button"
                className="np-btn np-btn-icon"
                disabled={busy || picked.length === 0}
                data-testid="onboarding-goals-continue"
                onClick={() => run(() => neveroverpayApi.onboardingIntents(allPicked ? ['all'] : picked))}
              >
                Continue with {picked.length === 0 ? 'your goals' : allPicked ? 'everything' : `${picked.length} goal${picked.length === 1 ? '' : 's'}`} <ArrowRight size={15} />
              </button>
            </div>
          </section>
        ) : null}

        {step === 'connect' ? (
          <section className="np-card">
            <h1>Connect Gmail to find bills, receipts, refunds, and renewals.</h1>
            <p className="np-muted">BilledTwice will look for:</p>
            <ul className="np-list" style={{ marginTop: 8 }}>
              <li>Purchase and return emails</li>
              <li>Billing and price-change notices</li>
              <li>Subscription and renewal messages</li>
              <li>Merchant support conversations</li>
              <li>Refund and credit confirmations</li>
            </ul>
            <p className="np-muted" style={{ marginTop: 16 }}>We will not send messages without your approval. This step only requests Gmail read access.</p>
            {data?.gmail?.forwarding_address ? (
              <p className="np-muted" style={{ marginTop: 16 }}>
                You can also forward mail to <code>{data.gmail.forwarding_address}</code>
              </p>
            ) : null}
            <ConsentGate
              kind="gmail_read"
              consents={data?.consents}
              busy={busy}
              blocked={data?.gmail?.configured ? '' : 'Gmail connect is not available on this server yet. Skip and paste a company email instead.'}
              actionLabel="Connect Gmail"
              onAllow={async () => {
                await run(async () => {
                  await neveroverpayApi.grantConsent('gmail_read');
                  if (!data?.gmail?.configured) return neveroverpayApi.onboarding();
                  await neveroverpayApi.onboardingInbox('gmail');
                  const row = await neveroverpayApi.gmailConnectUrl(false);
                  window.location.href = row.url;
                  return null;
                });
              }}
            >
              <div className="np-actions">
                <button
                  type="button"
                  className="np-btn"
                  disabled={busy || !data?.gmail?.configured}
                  onClick={() => run(async () => {
                    await neveroverpayApi.onboardingInbox('gmail');
                    const row = await neveroverpayApi.gmailConnectUrl(false);
                    window.location.href = row.url;
                    return null;
                  })}
                >
                  Connect Gmail
                </button>
              </div>
            </ConsentGate>
            <div className="np-actions">
              <button type="button" className="np-btn ghost" disabled={busy} onClick={() => run(() => neveroverpayApi.onboardingInbox('skip'))}>
                Skip for now
              </button>
            </div>
          </section>
        ) : null}

        {step === 'bank' ? (
          <section className="np-card">
            <h1>Connect your bank account to verify savings.</h1>
            <p className="np-muted">This helps BilledTwice:</p>
            <ul className="np-list" style={{ marginTop: 8 }}>
              <li>Detect recurring subscriptions</li>
              <li>Identify duplicate charges</li>
              <li>Confirm refunds and merchant credits</li>
              <li>Verify canceled subscriptions stop charging</li>
              <li>Track bill increases over time</li>
            </ul>
            <p className="np-muted" style={{ marginTop: 16 }}>
              We do not move money or make payments. We only read transaction activity you authorize.
            </p>
            {data?.plaid?.connected ? (
              <p className="np-muted" style={{ marginTop: 12 }}>Bank connected. Refunds can be verified against live transactions.</p>
            ) : null}
            {data?.plaid?.connected ? (
              <div className="np-actions">
                <button type="button" className="np-btn" disabled={busy} onClick={() => run(() => neveroverpayApi.onboardingBank('plaid'))}>
                  Continue
                </button>
              </div>
            ) : (
              <ConsentGate
                kind="bank_match"
                consents={data?.consents}
                busy={busy}
                blocked={data?.plaid?.configured ? '' : 'Bank connect is not available on this server yet. Skip for now — you can still upload a statement later.'}
                actionLabel="Connect Bank Securely"
                onAllow={async () => {
                  await run(async () => {
                    await neveroverpayApi.grantConsent('bank_match');
                    if (!data?.plaid?.configured) return neveroverpayApi.onboarding();
                    const tokenRow = await neveroverpayApi.plaidLinkToken();
                    const publicToken = await openPlaidLink({ linkToken: tokenRow.link_token });
                    if (!publicToken) return neveroverpayApi.onboarding();
                    await neveroverpayApi.plaidExchange(publicToken);
                    return neveroverpayApi.onboarding();
                  });
                }}
              >
                <div className="np-actions">
                  <button
                    type="button"
                    className="np-btn"
                    disabled={busy || !data?.plaid?.configured}
                    onClick={() => run(async () => {
                      const tokenRow = await neveroverpayApi.plaidLinkToken();
                      const publicToken = await openPlaidLink({ linkToken: tokenRow.link_token });
                      if (!publicToken) return neveroverpayApi.onboarding();
                      await neveroverpayApi.plaidExchange(publicToken);
                      return neveroverpayApi.onboarding();
                    })}
                  >
                    Connect Bank Securely
                  </button>
                </div>
              </ConsentGate>
            )}
            <div className="np-actions">
              <button type="button" className="np-btn ghost" disabled={busy} onClick={() => run(() => neveroverpayApi.onboardingBank('skip'))}>
                Skip for now
              </button>
            </div>
          </section>
        ) : null}

        {step === 'policy' ? (
          <section className="np-card np-rules-card">
            <h1>Set your protection rules</h1>
            <p className="np-muted np-rules-lead">These floors decide what we surface. We still never send, cancel, or accept anything without your OK.</p>
            <div className="np-rule-grid">
              <label className="np-rule-field">
                <span className="np-rule-name">Annual renewal alert</span>
                <span className="np-muted">Alert me when an annual renewal at or above this amount is coming up.</span>
                <span className="np-affix">
                  <span className="np-affix-mark">$</span>
                  <input
                    className="np-input"
                    type="number"
                    min={5}
                    step="1"
                    inputMode="decimal"
                    value={dollarsInput(policy.minimumAlertValueCents)}
                    onChange={(e) => setPolicy((prev) => ({ ...prev, minimumAlertValueCents: Math.round(Number(e.target.value || 0) * 100) }))}
                  />
                </span>
              </label>
              <label className="np-rule-field">
                <span className="np-rule-name">Bill increase flag</span>
                <span className="np-muted">Flag a bill if it rises by at least this percent.</span>
                <span className="np-affix">
                  <input
                    className="np-input"
                    type="number"
                    min={1}
                    max={50}
                    step="1"
                    inputMode="numeric"
                    value={policy.maximumAcceptedPriceIncreasePercent}
                    onChange={(e) => setPolicy((prev) => ({ ...prev, maximumAcceptedPriceIncreasePercent: Number(e.target.value || 10) }))}
                  />
                  <span className="np-affix-mark end">%</span>
                </span>
              </label>
              <label className="np-rule-field">
                <span className="np-rule-name">Renewal alert window</span>
                <span className="np-muted">How many days before a renewal we should warn you.</span>
                <span className="np-affix">
                  <input
                    className="np-input"
                    type="number"
                    min={7}
                    max={90}
                    step="1"
                    inputMode="numeric"
                    value={policy.renewalAlertDays}
                    onChange={(e) => setPolicy((prev) => ({ ...prev, renewalAlertDays: Number(e.target.value || 30) }))}
                  />
                  <span className="np-affix-mark end">days</span>
                </span>
              </label>
            </div>
            <div className="np-rule-block">
              <h2>For merchant messages</h2>
              <label className="np-check"><input type="checkbox" checked readOnly /> Always ask before sending</label>
              <label className="np-check"><input type="checkbox" checked readOnly /> Never accept a new contract</label>
              <label className="np-check">
                <input
                  type="checkbox"
                  checked={!policy.allowStoreCreditWithoutApproval}
                  onChange={(e) => setPolicy((prev) => ({ ...prev, allowStoreCreditWithoutApproval: !e.target.checked }))}
                />
                Never accept store credit without asking
              </label>
              <label className="np-check">
                <input
                  type="checkbox"
                  checked={policy.alertBeforeReturnWindowsExpire !== false}
                  onChange={(e) => setPolicy((prev) => ({ ...prev, alertBeforeReturnWindowsExpire: e.target.checked }))}
                />
                Alert me before return windows expire
              </label>
            </div>
            <div className="np-rule-block">
              <h2>Optional</h2>
              <label className="np-check">
                <input
                  type="checkbox"
                  checked={Boolean(policy.allowHumanOperator)}
                  onChange={(e) => setPolicy((prev) => ({ ...prev, allowHumanOperator: e.target.checked }))}
                />
                I allow a trained support specialist to help with approved recovery cases
              </label>
            </div>
            <div className="np-actions">
              <button type="button" className="np-btn" disabled={busy} onClick={() => run(() => neveroverpayApi.onboardingPolicy(policy))}>
                Save rules and start scan
              </button>
            </div>
          </section>
        ) : null}

        {step === 'scan' ? (
          <section className="np-card">
            <h1>
              {lookFirst.length
                ? `Searching for ${lookFirst[0].toLowerCase()}…`
                : 'Scanning Gmail and your bank for savings…'}
            </h1>
            <p className="np-muted">
              Gmail takes about 1–3 minutes for recent mail. Bank sync usually takes 30 seconds to a few minutes.
              We show first results as soon as this pass finishes. A deeper 12-month scan keeps running after that — we will not make you wait on it.
            </p>
            <div className="np-choice-stack" style={{ marginTop: 16 }}>
              <div className="np-choice">
                <strong>Gmail scan</strong>
                <span className="np-muted" style={{ display: 'block', marginTop: 6 }}>
                  {(data?.scan?.gmail?.status === 'done' && 'Done')
                    || (data?.scan?.gmail?.status === 'running' && 'Searching recent mail…')
                    || (data?.scan?.gmail?.status === 'skipped' && 'Skipped — Gmail is not connected')
                    || (data?.scan?.gmail?.status === 'error' && (data.scan.gmail.error || 'Could not finish'))
                    || data?.scan?.gmail?.hint
                    || '1–3 minutes for high-priority recent emails'}
                  {data?.scan?.gmail?.ingested ? ` · ${data.scan.gmail.ingested} messages` : ''}
                </span>
              </div>
              <div className="np-choice">
                <strong>Plaid scan</strong>
                <span className="np-muted" style={{ display: 'block', marginTop: 6 }}>
                  {(data?.scan?.plaid?.status === 'done' && 'Done')
                    || (data?.scan?.plaid?.status === 'running' && 'Syncing transactions…')
                    || (data?.scan?.plaid?.status === 'skipped' && 'Skipped — bank is not connected')
                    || (data?.scan?.plaid?.status === 'error' && (data.scan.plaid.error || 'Could not finish'))
                    || data?.scan?.plaid?.hint
                    || '30 seconds to several minutes, depending on institution'}
                  {data?.scan?.plaid?.imported ? ` · ${data.scan.plaid.imported} transactions` : ''}
                </span>
              </div>
            </div>
            <ul className="np-list" style={{ marginTop: 16 }}>
              {(data?.scan?.checks || []).map((check) => (
                <li key={check.id}>{check.done ? '✓' : '○'} {check.label}</li>
              ))}
            </ul>
            {data?.scan?.status === 'needs_mail' || (!data?.gmail?.connected && !data?.plaid?.connected) ? (
              <ConsentGate
                kind="documents"
                consents={data?.consents}
                busy={busy}
                actionLabel="Allow and continue"
                onAllow={() => run(async () => {
                  await neveroverpayApi.grantConsent('documents');
                  return neveroverpayApi.onboarding();
                })}
              >
                <form
                  style={{ marginTop: 16 }}
                  onSubmit={(event) => {
                    event.preventDefault();
                    if (!paste.trim()) return;
                    run(async () => {
                      await neveroverpayApi.ingestText(paste);
                      setPaste('');
                      return neveroverpayApi.onboardingScan();
                    });
                  }}
                >
                  <p className="np-muted">Paste a real company email, or upload one, so we can find a first case.</p>
                  <textarea className="np-input" rows={5} value={paste} onChange={(e) => setPaste(e.target.value)} placeholder="Paste a refund, renewal, or delivery email…" />
                  <div className="np-actions">
                    <button type="submit" className="np-btn" disabled={busy}>Check this email</button>
                    <label className="np-btn ghost">
                      Upload document
                      <input
                        type="file"
                        accept=".txt,.eml,.md,.pdf,.html,.png,.jpg,.jpeg,.webp"
                        hidden
                        onChange={(event) => {
                          const file = event.target.files?.[0];
                          if (!file) return;
                          run(async () => {
                            await neveroverpayApi.upload(file);
                            return neveroverpayApi.onboardingScan();
                          });
                          event.target.value = '';
                        }}
                      />
                    </label>
                  </div>
                </form>
              </ConsentGate>
            ) : null}
            <div className="np-actions">
              {data?.gmail?.connected || data?.plaid?.connected ? (
                <button type="button" className="np-btn" disabled={busy || ['running', 'deep_running'].includes(data?.scan?.status)} onClick={() => run(() => neveroverpayApi.onboardingScan())}>
                  {['running', 'deep_running'].includes(data?.scan?.status) ? 'Scanning…' : 'Start first scan'}
                </button>
              ) : (
                <button type="button" className="np-btn ghost" disabled={busy} onClick={() => run(() => neveroverpayApi.onboardingComplete())}>
                  Continue without mail or bank
                </button>
              )}
            </div>
          </section>
        ) : null}

        {step === 'review' ? (
          <section className="np-card">
            <h1>
              {(data?.ranked || []).length
                ? `We found ${(data.ranked.length)} ${data.ranked.length === 1 ? 'opportunity' : 'opportunities'} worth reviewing.`
                : 'No high-confidence opportunities yet.'}
            </h1>
            <p className="np-muted">
              AI can analyze and draft. It still will not send, cancel, or accept anything until you approve the exact action.
            </p>
            {data?.scan?.status === 'deep_running' || data?.scan?.deep?.status === 'running' ? (
              <p className="np-muted">A deeper scan is still running in the background. New recoveries can appear on Home.</p>
            ) : null}
            {(data?.ranked || []).map((c, index) => (
              <button
                key={c.id}
                type="button"
                className="np-case-row"
                style={{ width: '100%', background: 'transparent', color: 'inherit', textAlign: 'left', cursor: 'pointer' }}
                onClick={() => navigate(`/cases/${c.id}`)}
              >
                <div>
                  <div className="np-pill">{index + 1}. {workflowLabel(c.workflow)}</div>
                  <h3 style={{ marginTop: 8 }}>{c.merchant} · {c.estimated_display}</h3>
                  <p className="np-muted">{c.why_now}</p>
                </div>
                <div className="np-muted">{stateLabel(c.state)}</div>
              </button>
            ))}
            {!(data?.ranked || []).length ? (
              <p className="np-muted" style={{ marginTop: 12 }}>
                Connect Gmail or paste a refund / renewal / delivery email. Low-value or low-confidence items stay hidden until they are actionable.
              </p>
            ) : null}
            <div className="np-actions">
              <button type="button" className="np-btn" disabled={busy} onClick={() => run(() => neveroverpayApi.onboardingComplete())}>
                Go to my dashboard
              </button>
            </div>
          </section>
        ) : null}
      </div>
    </div>
  );
}
